/**
 * M4 CONTROL-FEEL: virtual-stick mouse model (Elite-style "relative mouse").
 *
 * Mouse deltas accumulate into a PERSISTENT stick deflection (-1..1 per axis)
 * instead of rotating the ship directly. The deflection is consumed as a rate
 * command by the rigid-body torque servo (commandTorque), so the ship turns
 * through its physics model — inertia on spin-up, damping on release.
 *
 * Why not the older per-frame-delta → rate mapping (pre-b7b41a3)? That divided
 * the instantaneous delta by frameDt, which is framerate-coupled and spiky: a
 * one-frame flick at 144 Hz commanded a huge rate for one frame, and a held
 * mouse commanded zero. Deflection-as-state is framerate-independent by
 * construction: a stationary deflected stick holds a constant turn.
 *
 * PURE sim-core module — no DOM, no Three.js, no randomness, no wall clock.
 */

export interface StickState {
  /** Pitch deflection, -1..1 (raw, pre-deadzone/expo). */
  pitch: number;
  /** Yaw deflection, -1..1 (raw, pre-deadzone/expo). */
  yaw: number;
}

export interface StickParams {
  /** Pixels of mouse travel from center to full (±1) deflection. */
  pxToFull: number;
  /** Exponential relax of deflection toward 0 (1/s). 0 = pure relative stick. */
  relaxPerSec: number;
  /** Raw |deflection| treated as 0; output rescales smoothly past it. */
  deadzone: number;
  /** Response-curve exponent (>=1). 1 = linear; higher = finer center control. */
  expo: number;
  /** Per-frame |delta| clamp in px (guards pointer-lock acquire spikes). */
  maxPxPerFrame: number;
}

export const DEFAULT_STICK: StickParams = {
  pxToFull: 220,
  // STICKY stick (Elite relative-mouse default, operator-confirmed in the
  // session-5 playtest): deflection HOLDS until counter-moved. Raise via the
  // ?tune=1 panel for a self-centering stick.
  relaxPerSec: 0,
  deadzone: 0.03,
  expo: 1.4,
  maxPxPerFrame: 300,
};

export const stickZero = (): StickState => ({ pitch: 0, yaw: 0 });

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/**
 * Advance the stick one render frame: decay the held deflection over dt, then
 * accumulate this frame's mouse delta. dt <= 0 skips decay but STILL
 * accumulates — a zero-substep high-refresh frame never drops a delta
 * (preserves the M1.1 BUG-1 invariant).
 *
 * Sign convention is neutral: positive dxPx → positive yaw deflection. The
 * caller applies its own input negations.
 */
export const stickUpdate = (
  s: StickState,
  dxPx: number,
  dyPx: number,
  dt: number,
  p: StickParams,
): StickState => {
  const decay = p.relaxPerSec > 0 && dt > 0 ? Math.exp(-p.relaxPerSec * dt) : 1;
  const dYaw = clamp(dxPx, -p.maxPxPerFrame, p.maxPxPerFrame) / p.pxToFull;
  const dPitch = clamp(dyPx, -p.maxPxPerFrame, p.maxPxPerFrame) / p.pxToFull;
  return {
    pitch: clamp(s.pitch * decay + dPitch, -1, 1),
    yaw: clamp(s.yaw * decay + dYaw, -1, 1),
  };
};

/** Deadzone (with rescale so output stays continuous) then expo, per axis. */
const shapeAxis = (v: number, p: StickParams): number => {
  const m = Math.max(0, (Math.abs(v) - p.deadzone) / (1 - p.deadzone));
  return Math.sign(v) * Math.min(1, m) ** p.expo;
};

/** Shaped -1..1 rate command per axis (what feeds the torque servo). */
export const stickOutput = (s: StickState, p: StickParams): { pitch: number; yaw: number } => ({
  pitch: shapeAxis(s.pitch, p),
  yaw: shapeAxis(s.yaw, p),
});
